/**
 * WP-V2-WC — tap-to-move + strzałki/WASD + przytrzymanie.
 * Kolizje: prostokąty z danych sceny. Napędza WalkableTarget (moveTo/setVelocity/container).
 */
import Phaser from 'phaser';
import { stepWalkState, type WalkControllerState } from './walkState.js';
import { nudgeOutOfColliders } from './walkCollision.js';
import {
  DEFAULT_AGENT_RADIUS,
  DEFAULT_WALK_SPEED,
  type ColliderRect,
  type WalkableTarget,
  type WorldMoveBounds,
} from './types.js';

export type { WalkControllerState } from './walkState.js';
export { stepWalkState } from './walkState.js';

export type WalkControllerOpts = {
  colliders?: readonly ColliderRect[];
  bounds?: WorldMoveBounds;
  speed?: number;
  agentRadius?: number;
  /** Gdy false — nie podpina inputu Phaser (tylko API programowe / testy). */
  enableInput?: boolean;
};

export class WalkController {
  private state: WalkControllerState;
  private colliders: ColliderRect[];
  private bounds: WorldMoveBounds | undefined;
  private speed: number;
  private agentRadius: number;
  private keys: {
    up?: Phaser.Input.Keyboard.Key;
    down?: Phaser.Input.Keyboard.Key;
    left?: Phaser.Input.Keyboard.Key;
    right?: Phaser.Input.Keyboard.Key;
    w?: Phaser.Input.Keyboard.Key;
    a?: Phaser.Input.Keyboard.Key;
    s?: Phaser.Input.Keyboard.Key;
    d?: Phaser.Input.Keyboard.Key;
  } = {};
  private pointerDown = false;
  private pointerWorld = { x: 0, y: 0 };
  private unsub: Array<() => void> = [];

  constructor(
    private readonly scene: Phaser.Scene | null,
    private readonly target: WalkableTarget,
    opts: WalkControllerOpts = {},
  ) {
    this.colliders = opts.colliders ? [...opts.colliders] : [];
    this.bounds = opts.bounds;
    this.speed = opts.speed ?? DEFAULT_WALK_SPEED;
    this.agentRadius = opts.agentRadius ?? DEFAULT_AGENT_RADIUS;

    // WAVE3 G1: jeśli spawn jest w colliderze — wypchnij poza (nudge).
    let x = target.container.x;
    let y = target.container.y;
    if (this.colliders.length > 0) {
      const free = nudgeOutOfColliders(
        x,
        y,
        this.colliders,
        this.agentRadius,
        this.bounds,
      );
      x = free.x;
      y = free.y;
      const c = target.container;
      if (typeof c.setPosition === 'function') {
        c.setPosition(x, y);
      } else {
        c.x = x;
        c.y = y;
      }
    }

    this.state = { x, y, tx: x, ty: y, mode: 'idle', vx: 0, vy: 0 };

    if (scene && opts.enableInput !== false) {
      this.bindInput(scene);
    }
  }

  setColliders(colliders: readonly ColliderRect[]): void {
    this.colliders = [...colliders];
  }

  setBounds(bounds: WorldMoveBounds | undefined): void {
    this.bounds = bounds;
  }

  get position(): { x: number; y: number } {
    return { x: this.state.x, y: this.state.y };
  }

  get isMoving(): boolean {
    return this.state.mode !== 'idle';
  }

  /** Programowy tap-to-move (jak klik w świat). */
  moveTo(x: number, y: number): void {
    this.state = {
      ...this.state,
      tx: x,
      ty: y,
      mode: 'path',
    };
    this.target.moveTo(x, y);
  }

  /** Programowa prędkość (np. wirtualny drążek). */
  setVelocityInput(vx: number, vy: number): void {
    if (Math.hypot(vx, vy) < 1e-6) {
      this.state = { ...this.state, vx: 0, vy: 0, mode: 'idle' };
      this.target.setVelocity(0, 0);
      return;
    }
    this.state = {
      ...this.state,
      vx,
      vy,
      mode: 'velocity',
      tx: this.state.x,
      ty: this.state.y,
    };
  }

  /** Snapshot stanu (testy). */
  getState(): WalkControllerState {
    return { ...this.state };
  }

  /** Wstrzyknięcie stanu (testy). */
  setStateForTest(partial: Partial<WalkControllerState>): void {
    this.state = { ...this.state, ...partial };
  }

  update(dtSec: number): void {
    if (this.scene) {
      const k = this.readKeyboardVector();
      if (k) {
        this.state = {
          ...this.state,
          vx: k.x,
          vy: k.y,
          mode: 'velocity',
        };
      } else if (this.pointerDown) {
        const dx = this.pointerWorld.x - this.state.x;
        const dy = this.pointerWorld.y - this.state.y;
        if (Math.hypot(dx, dy) > 8) {
          this.state = {
            ...this.state,
            vx: dx,
            vy: dy,
            mode: 'velocity',
          };
        }
      }
    }

    this.state = stepWalkState(this.state, dtSec, this.colliders, {
      speed: this.speed,
      agentRadius: this.agentRadius,
      bounds: this.bounds,
    });

    this.applyToTarget();
  }

  private applyToTarget(): void {
    const { x, y, vx, vy } = this.state;
    const c = this.target.container;
    if (typeof c.setPosition === 'function') {
      c.setPosition(x, y);
    } else {
      c.x = x;
      c.y = y;
    }
    if (this.state.mode === 'idle') {
      this.target.setVelocity(0, 0);
    } else {
      this.target.setVelocity(vx, vy);
    }
  }

  private readKeyboardVector(): { x: number; y: number } | null {
    const { up, down, left, right, w, a, s, d } = this.keys;
    let x = 0;
    let y = 0;
    if (left?.isDown || a?.isDown) x -= 1;
    if (right?.isDown || d?.isDown) x += 1;
    if (up?.isDown || w?.isDown) y -= 1;
    if (down?.isDown || s?.isDown) y += 1;
    if (x === 0 && y === 0) return null;
    return { x, y };
  }

  private bindInput(scene: Phaser.Scene): void {
    const kb = scene.input.keyboard;
    if (kb) {
      this.keys = {
        up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
        down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
        left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
        right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
        w: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
        a: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        s: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
        d: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      };
    }

    /**
     * World point z pointera — Scale.FIT.
     * Używamy positionToCamera (nie surowe clientX), żeby hit-test
     * pokrywał się z kursorem po literboxie (WAVE7-A/E offset w lewo).
     */
    const worldOf = (pointer: Phaser.Input.Pointer) => {
      const cam = scene.cameras.main;
      // Phaser 3: pointer.x/y = game coords po scale managerze.
      // positionToCamera jest równoważne getWorldPoint dla main cam.
      if (typeof pointer.positionToCamera === 'function') {
        const p = pointer.positionToCamera(cam) as { x: number; y: number };
        return { x: p.x, y: p.y };
      }
      return cam.getWorldPoint(pointer.x, pointer.y);
    };
    const onDown = (pointer: Phaser.Input.Pointer) => {
      if (!pointer.leftButtonDown() && pointer.button !== 0) return;
      // nie ruszaj tapem w górny pasek HUD (saldo / telefon)
      if (pointer.y < 88) return;
      const wp = worldOf(pointer);
      this.pointerDown = true;
      this.pointerWorld = { x: wp.x, y: wp.y };
      this.moveTo(wp.x, wp.y);
    };
    const onMove = (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      const wp = worldOf(pointer);
      this.pointerWorld = { x: wp.x, y: wp.y };
      this.state = {
        ...this.state,
        mode: 'velocity',
        vx: wp.x - this.state.x,
        vy: wp.y - this.state.y,
      };
    };
    const onUp = () => {
      this.pointerDown = false;
      if (this.state.mode === 'velocity') {
        this.state = { ...this.state, mode: 'idle', vx: 0, vy: 0 };
        this.target.setVelocity(0, 0);
      }
    };

    scene.input.on('pointerdown', onDown);
    scene.input.on('pointermove', onMove);
    scene.input.on('pointerup', onUp);
    this.unsub.push(() => {
      scene.input.off('pointerdown', onDown);
      scene.input.off('pointermove', onMove);
      scene.input.off('pointerup', onUp);
    });
  }

  destroy(): void {
    for (const u of this.unsub) u();
    this.unsub = [];
    this.target.setVelocity(0, 0);
  }
}
